import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, RotateCcw, AlertTriangle, Search, Boxes } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTrash = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_collection: {} });
  const [registry, setRegistry] = useState({});
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState('all');
  const [query, setQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cQuery = collection && collection !== 'all' ? `collection=${collection}&` : '';
      const [{ data: t }, { data: s }, { data: r }] = await Promise.all([
        axios.get(`${API}/admin/trash?${cQuery}q=${encodeURIComponent(query)}`, { withCredentials: true }),
        axios.get(`${API}/admin/trash/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/archive/registry`, { withCredentials: true }),
      ]);
      setItems(t.items || []);
      setStats(s);
      setRegistry(r.collections || {});
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Failed to load trash');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection]);

  const onRestore = async (it) => {
    try {
      await axios.post(`${API}/admin/archive/${it.collection_key}/${it.id}/restore`, {}, { withCredentials: true });
      toast.success(`Restored: ${it.label}`);
      load();
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const onHardDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/admin/archive/${pendingDelete.collection_key}/${pendingDelete.id}`, { withCredentials: true });
      toast.success(`Permanently deleted: ${pendingDelete.label}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Hard delete failed');
    }
  };

  const collKeys = Object.keys(registry);

  return (
    <div className="p-6 space-y-6" data-testid="admin-trash">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Trash2 className="w-6 h-6 text-[#D4AF37]" />
            Trash <span className="text-[#A3A3A3] text-base">({stats.total})</span>
          </h1>
          <p className="text-[#A3A3A3] text-sm mt-1">
            Archived records across the system. Restore any item, or permanently delete (super-admin only).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#A3A3A3]" />
            <Input data-testid="trash-search" placeholder="Search…" value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              className="pl-8 bg-[#0A0A0A] border-[#262626] text-white w-56" />
          </div>
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white w-56" data-testid="trash-collection-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626] text-white max-h-72">
              <SelectItem value="all">All collections</SelectItem>
              {collKeys.map((k) => (
                <SelectItem key={k} value={k}>
                  {registry[k]?.label || k} ({stats.by_collection?.[k] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        {loading ? (
          <div className="p-8 text-[#A3A3A3] text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]" data-testid="trash-empty">
            <Boxes className="w-10 h-10 mx-auto mb-3 text-[#D4AF37]" />
            <p>Trash is empty.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#262626]">
            {items.map((it) => (
              <li key={`${it.collection_key}-${it.id}`} data-testid={`trash-row-${it.id}`} className="p-4 flex items-start gap-4 hover:bg-[#1a1a1a]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30">{it.collection_label}</Badge>
                  </div>
                  <p className="text-white text-sm mt-1 truncate">{it.label}</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    Archived {it.archived_at ? new Date(it.archived_at).toLocaleString() : '—'} by <span className="text-white">{it.archived_by_name || '—'}</span>
                  </p>
                  {it.archive_reason && <p className="text-[#A3A3A3] text-xs italic mt-1">"{it.archive_reason}"</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button size="sm" variant="outline" className="border-[#404040] text-white" data-testid={`restore-${it.id}`} onClick={() => onRestore(it)}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" data-testid={`hard-delete-${it.id}`} onClick={() => setPendingDelete(it)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete forever
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Permanent delete</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              This will permanently delete the record. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {pendingDelete && (
            <div className="space-y-2">
              <p className="text-sm">Collection: <span className="text-[#D4AF37]">{pendingDelete.collection_label}</span></p>
              <p className="text-sm truncate">Label: <span className="text-white">{pendingDelete.label}</span></p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button data-testid="confirm-hard-delete" className="bg-red-600 hover:bg-red-700 text-white" onClick={onHardDelete}>Delete forever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrash;
